import type { FC } from 'react'
import type { TunaPlugin } from '../plugin-types.js'
import type { RenderPluginsProps } from '../plugin-types.js'

type PluginRenderType = RenderPluginsProps['type']

/** Cache plugins that have a component for each render type */
const pluginsByTypeCache = new WeakMap<TunaPlugin[], Partial<Record<PluginRenderType, TunaPlugin[]>>>()

const getPluginsForType = (plugins: TunaPlugin[], type: PluginRenderType): TunaPlugin[] => {
  let typeMap = pluginsByTypeCache.get(plugins)
  if (!typeMap) {
    typeMap = {}
    pluginsByTypeCache.set(plugins, typeMap)
  }
  if (!typeMap[type]) {
    typeMap[type] = plugins.filter(p => {
      const components = p.components
      if (!components) {
        return false
      }
      switch (type) {
        case 'root':
          return Boolean(components.Root)
        case 'top':
          return Boolean(components.Top)
        case 'help':
          return Boolean(components.Help)
        case 'suggest':
          return Boolean(components.Suggest)
      }
    })
  }
  return typeMap[type] ?? []
}

const getComponent = (plugin: TunaPlugin, type: PluginRenderType) => {
  switch (type) {
    case 'root':
      return plugin.components?.Root
    case 'top':
      return plugin.components?.Top
    case 'help':
      return plugin.components?.Help
    case 'suggest':
      return plugin.components?.Suggest
  }
}

export const RenderPlugins: FC<RenderPluginsProps> = props => {
  const { type, className, editor, componentProps, onClose } = props

  const {
    meta: { pluginList: plugins },
  } = editor

  const matchingPlugins = getPluginsForType(plugins, type)

  return (
    <>
      {matchingPlugins.map(plugin => {
        const RenderComponent = getComponent(plugin, type)
        if (!RenderComponent) {
          return null
        }
        return (
          <RenderComponent
            key={plugin.key}
            {...componentProps}
            className={className}
            editor={editor}
            onClose={onClose}
          />
        )
      })}
    </>
  )
}
